import React, { useState, useEffect } from 'react';
import {
  View,
  Text,
  StyleSheet,
  SafeAreaView,
  TouchableOpacity,
  Image,
  ScrollView,
  TextInput,
  Modal,
  FlatList,
  Alert,
} from 'react-native';
import { ms, s, vs } from '../../../utils/responsive';
import { useNavigation, useRoute } from '@react-navigation/native';
import { RootStackNavigationProp } from '../../../types/navigation';
import { BottomNavBar } from '../../../components/BottomNavBar';
import { expenseService } from '../../../services/expenseService';

import { createLogger } from '../../../utils/logger';
const logger = createLogger('ExpensesInput');

const categories = [
  'Housing',
  'Taxes',
  'Utilities',
  'Insurance',
  'Credit Card',
  'Loan',
  'Healthcare',
  'Memberships',
  'Subscription',
  'Investments',
  'Personal',
  'Other'
];

const expenseTypes: { [key: string]: string[] } = {
  Housing: ['Lawn Care', 'Pool Care', 'Pest Control', 'HOA', 'Security', 'House Keeping', 'Other'],
  Taxes: ['Income', 'Property', 'Vehicle', 'Business', 'Other'],
  Utilities: ['Streaming TV', 'Electricity', 'Gas', 'Water', 'Garbage', 'Internet', 'Other'],
  Insurance: ['Homeowners', 'Car', 'Flood', 'Renters', 'Medical', 'Dental', 'Vision', 'Pet', 'Long-Term Care', 'Umbrella', 'Earthquake', 'Other'],
  "Credit Card": ['Mastercard', 'Visa', 'American Express', 'Discover', 'Other'],
  Loan: ['Mortgage', 'Auto', 'Business', 'Personal', 'Student', 'Boat', 'Home Equity', 'RV', 'Other'],
  Healthcare: ['Physical Therapy', 'Chiropractic Care', 'Orthodontics', 'Fertility Treatments', 'Home Health Services', 'Other'],
  Memberships: ['Online Course', 'Professional', 'Country Club', 'Gym', 'Museum', 'Zoo', 'Union', 'Other'],
  Subscription: ['Amazon', 'Music', 'Legal', 'Software', 'Audio Books', 'Meals Service', 'Health & Beauty', 'Other'],
  Investments: ['IRA', 'RothIRA', 'Life Insurance', 'Annuity', 'Mutual Fund', 'Real Estate', 'Timeshare', '529', 'Other'],
  Digital: ['Music', 'Photos', 'Domain Name', 'Storage', 'Other'],
  Other: ['Travel', 'Rewards', 'Pet', 'Misc']
};

const frequencyOptions = [
  'Daily',
  'Weekly',
  'Monthly',
  'Quarterly',
  'Bi-Annually',
  'Annually',
];

export const ExpensesInputScreen: React.FC = () => {
  const navigation = useNavigation<RootStackNavigationProp>();
  const route = useRoute();
  const [formData, setFormData] = useState({
    category: '',
    expenseType: '',
    expenseName: '',
    frequency: '',
    cost: '',
    provider: '',
    dueDate: '',
    accountNumber: '',
    notes: '',
  });
  const [showCategoryModal, setShowCategoryModal] = useState(false);
  const [showTypeModal, setShowTypeModal] = useState(false);
  const [showFrequencyModal, setShowFrequencyModal] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Format cost input to currency format
  const formatCurrency = (text: string) => {
    // Remove all non-numeric characters except decimal point
    const cleaned = text.replace(/[^0-9.]/g, '');

    // Handle multiple decimal points
    const parts = cleaned.split('.');
    if (parts.length > 2) {
      return formData.cost; // Return previous value if invalid
    }

    // Format the integer part with commas
    const integerPart = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');

    // Add decimal part if it exists
    if (parts.length === 2) {
      // Limit decimal places to 2
      const decimalPart = parts[1].slice(0, 2);
      return `$${integerPart}.${decimalPart}`;
    }

    // Add dollar sign
    return `$${integerPart}`;
  };

  // Parse currency string to number for submission
  const parseCurrency = (text: string): number => {
    const cleaned = text.replace(/[$,]/g, '');
    return parseFloat(cleaned) || 0;
  };

  // Check if editing
  const params = route.params as any;
  const isEditing = params?.expense;

  // Handle route params (category pre-selection or expense editing)
  useEffect(() => {
    const params = route.params as any;

    // If editing existing expense
    if (params?.expense) {
      const expense = params.expense;
      setFormData({
        category: expense.category || '',
        expenseType: expense.provider || '',
        expenseName: expense.title || '',
        frequency: expense.dueDate || '',
        cost: expense.amount ? formatCurrency(expense.amount.toString()) : '',
        provider: expense.provider || '',
        dueDate: expense.dueDate || '',
        accountNumber: expense.accountNumber || '',
        notes: expense.notes || '',
      });
    }

    // If category is pre-selected (from dashboard)
    if (params?.category) {
      setFormData(prev => ({ ...prev, category: params.category }));
    }
  }, [route.params]);

  const handleSubmit = async () => {
    if (!formData.category || !formData.expenseName || !formData.cost) {
      Alert.alert('Missing Information', 'Please fill in all required fields.');
      return;
    }

    setIsSubmitting(true);
    try {
      const params = route.params as any;
      const isEditing = params?.expense;
      
      const expenseData = {
        title: formData.expenseName,
        amount: parseCurrency(formData.cost),
        category: formData.category.toLowerCase(),
        provider: formData.provider,
        dueDate: formData.dueDate,
        accountNumber: formData.accountNumber,
        notes: formData.notes,
      };
      
      let expenseId: number;
      
      if (isEditing) {
        // Update existing expense
        logger.debug('Updating expense:', expenseData);
        const updatedExpense = await expenseService.updateExpense(params.expense.id, expenseData);
        expenseId = updatedExpense.id;
      } else {
        // Create new expense
        logger.debug('Creating expense:', expenseData);
        const newExpense = await expenseService.storeExpense(expenseData);
        expenseId = newExpense.id;
      }

      Alert.alert(
        'Success',
        `Expense ${isEditing ? 'updated' : 'saved'} successfully!`,
        [
          {
            text: 'OK',
            onPress: () => {
              if (isEditing) {
                // For editing, go back to the data screen with updated expense
                const updatedExpenseForDisplay = {
                  id: params.expense.id,
                  ...expenseData,
                };
                navigation.navigate('ExpensesDataScreen' as any, { expense: updatedExpenseForDisplay });
              } else {
                // For new expense, go to dashboard
                navigation.navigate('ExpensesDashboardScreen' as any);
              }
            }
          }
        ]
      );
    } catch (error: any) {
      logger.error('Error saving expense:', error);
      let errorMessage = 'Failed to save expense. Please try again.';
      
      if (error.message) {
        if (error.message.includes('Network Error') || error.message.includes('network')) {
          errorMessage = 'Network error. Expense saved locally and will sync when connection is restored.';
        } else if (error.message.includes('Server error')) {
          errorMessage = `Server error: ${error.message}`;
        } else {
          errorMessage = error.message;
        }
      }
      
      Alert.alert('Error', errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <SafeAreaView style={styles.container}>
      <View style={styles.header}>
        <TouchableOpacity
          style={styles.backButton}
          onPress={() => navigation.goBack()}
        >
          <Image
            source={require('../../../assets/icons/back.png')}
            style={[styles.backIcon, { tintColor: '#fff' }]}
          />
        </TouchableOpacity>
      </View>

      <View style={styles.topSection}>
        <Image
          source={require('../../../assets/icons/keyimage.png')}
          style={styles.backgroundKey}
        />
        <Image
          source={require('../../../assets/logo.png')}
          style={styles.logo}
          resizeMode="contain"
        />
        <Text style={styles.title}>Add Expense Details</Text>
      </View>

      <View style={styles.inputContainer}>
        <ScrollView style={styles.scrollView} contentContainerStyle={styles.scrollViewContent} keyboardShouldPersistTaps="handled" automaticallyAdjustKeyboardInsets={true}>
          <View style={styles.inputGroup}>
            <TouchableOpacity
              style={styles.dropdownInput}
              onPress={() => setShowCategoryModal(true)}
            >
              <Text style={[styles.inputText, !formData.category && styles.placeholderText]}>
                {formData.category || 'Category'}
              </Text>
              <Image
                source={require('../../../assets/icons/down.png')}
                style={styles.dropdownIcon}
              />
            </TouchableOpacity>
          </View>

          <View style={styles.inputGroup}>
            <TouchableOpacity
              style={[styles.dropdownInput, !formData.category && styles.disabledDropdown]}
              onPress={() => formData.category && setShowTypeModal(true)}
              disabled={!formData.category}
            >
              <Text style={[styles.inputText, (!formData.expenseType || !formData.category) && styles.placeholderText]}>
                {formData.expenseType || 'Type of Expense'}
              </Text>
              <Image
                source={require('../../../assets/icons/down.png')}
                style={[styles.dropdownIcon, !formData.category && styles.disabledIcon]}
              />
            </TouchableOpacity>
          </View>

          <View style={styles.inputGroup}>
            <View style={styles.inputWrapper}>
              <TextInput
                style={styles.input}
                value={formData.expenseName}
                onChangeText={(text) => setFormData({ ...formData, expenseName: text })}
                placeholder="Expense Name"
                placeholderTextColor="#A9A9A9"
              />
            </View>
          </View>

          <View style={styles.inputGroup}>
            <View style={styles.inputWrapper}>
              <TextInput
                style={styles.input}
                value={formData.cost}
                onChangeText={(text) => {
                  const formatted = formatCurrency(text);
                  setFormData({ ...formData, cost: formatted });
                }}
                placeholder="Cost (e.g., $1,000.00)"
                placeholderTextColor="#A9A9A9"
                keyboardType="decimal-pad"
              />
            </View>
          </View>

          <View style={styles.inputGroup}>
            <TouchableOpacity
              style={styles.dropdownInput}
              onPress={() => setShowFrequencyModal(true)}
            >
              <Text style={[styles.inputText, !formData.frequency && styles.placeholderText]}>
                {formData.frequency || 'Frequency'}
              </Text>
              <Image
                source={require('../../../assets/icons/down.png')}
                style={styles.dropdownIcon}
              />
            </TouchableOpacity>
          </View>

          <View style={styles.inputGroup}>
            <View style={styles.inputWrapper}>
              <TextInput
                style={styles.input}
                value={formData.provider}
                onChangeText={(text) => setFormData({ ...formData, provider: text })}
                placeholder="Provider/Company"
                placeholderTextColor="#A9A9A9"
              />
            </View>
          </View>

          <View style={styles.inputGroup}>
            <View style={styles.inputWrapper}>
              <TextInput
                style={styles.input}
                value={formData.dueDate}
                onChangeText={(text) => setFormData({ ...formData, dueDate: text })}
                placeholder="Due Date (optional)"
                placeholderTextColor="#A9A9A9"
              />
            </View>
          </View>

          <View style={styles.inputGroup}>
            <View style={styles.inputWrapper}>
              <TextInput
                style={styles.input}
                value={formData.accountNumber}
                onChangeText={(text) => setFormData({ ...formData, accountNumber: text })}
                placeholder="Account Number (optional)"
                placeholderTextColor="#A9A9A9"
              />
            </View>
          </View>

          <View style={styles.inputGroup}>
            <View style={styles.inputWrapper}>
              <TextInput
                style={[styles.input, styles.notesInput]}
                value={formData.notes}
                onChangeText={(text) => setFormData({ ...formData, notes: text })}
                placeholder="Notes (optional)"
                placeholderTextColor="#A9A9A9"
                multiline={true}
                numberOfLines={3}
              />
            </View>
          </View>

          <TouchableOpacity
            style={[styles.submitButton, isSubmitting && styles.disabledButton]}
            onPress={handleSubmit}
            disabled={isSubmitting}
          >
            <Text style={styles.submitButtonText}>
              {isSubmitting ? 'Saving...' : (isEditing ? 'Update' : 'Submit')}
            </Text>
          </TouchableOpacity>

          <View style={styles.bottomPadding} />
        </ScrollView>
      </View>

      <Modal
        visible={showCategoryModal}
        transparent={true}
        animationType="slide"
      >
        <View style={styles.modalContainer}>
          <View style={styles.modalContent}>
            <Text style={styles.modalTitle}>Select Category</Text>
            <FlatList
              data={categories}
              keyExtractor={(item) => item}
              renderItem={({ item }) => (
                <TouchableOpacity
                  style={styles.categoryItem}
                  onPress={() => {
                    setFormData({ ...formData, category: item, expenseType: '' });
                    setShowCategoryModal(false);
                  }}
                >
                  <Text style={styles.categoryText}>{item}</Text>
                </TouchableOpacity>
              )}
            />
            <TouchableOpacity
              style={styles.closeButton}
              onPress={() => setShowCategoryModal(false)}
            >
              <Text style={styles.closeButtonText}>Cancel</Text>
            </TouchableOpacity>
          </View>
        </View>
      </Modal>

      <Modal
        visible={showTypeModal}
        transparent={true}
        animationType="slide"
      >
        <View style={styles.modalContainer}>
          <View style={styles.modalContent}>
            <Text style={styles.modalTitle}>Select Type</Text>
            <FlatList
              data={formData.category ? expenseTypes[formData.category] || [] : []}
              keyExtractor={(item) => item}
              renderItem={({ item }) => (
                <TouchableOpacity
                  style={styles.categoryItem}
                  onPress={() => {
                    setFormData({ ...formData, expenseType: item });
                    setShowTypeModal(false);
                  }}
                >
                  <Text style={styles.categoryText}>{item}</Text>
                </TouchableOpacity>
              )}
            />
            <TouchableOpacity
              style={styles.closeButton}
              onPress={() => setShowTypeModal(false)}
            >
              <Text style={styles.closeButtonText}>Cancel</Text>
            </TouchableOpacity>
          </View>
        </View>
      </Modal>

      <Modal
        visible={showFrequencyModal}
        transparent={true}
        animationType="slide"
      >
        <View style={styles.modalContainer}>
          <View style={styles.modalContent}>
            <Text style={styles.modalTitle}>Select Frequency</Text>
            <FlatList
              data={frequencyOptions}
              keyExtractor={(item) => item}
              renderItem={({ item }) => (
                <TouchableOpacity
                  style={styles.categoryItem}
                  onPress={() => {
                    setFormData({ ...formData, frequency: item });
                    setShowFrequencyModal(false);
                  }}
                >
                  <Text style={styles.categoryText}>{item}</Text>
                </TouchableOpacity>
              )}
            />
            <TouchableOpacity
              style={styles.closeButton}
              onPress={() => setShowFrequencyModal(false)}
            >
              <Text style={styles.closeButtonText}>Cancel</Text>
            </TouchableOpacity>
          </View>
        </View>
      </Modal>

      <BottomNavBar />
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#29BFC0',
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: ms(15),
    paddingTop: ms(30),
    position: 'relative',
  },
  backButton: {
    position: 'absolute',
    left: ms(50),
    padding: ms(15),
    paddingTop: ms(50),
    zIndex: 1,
  },
  backIcon: {
    width: s(32),
    height: s(32),
    tintColor: '#fff',
  },
  topSection: {
    alignItems: 'center',
    paddingVertical: ms(20),
  },
  backgroundKey: {
    position: 'absolute',
    width: '85%',
    height: '85%',
    opacity: 0.1,
  },
  logo: {
    width: s(125),
    height: s(125),
    marginBottom: ms(16),
  },
  title: {
    fontSize: ms(24, 0.3),
    fontWeight: 'bold',
    color: '#FFFFFF',
    marginBottom: ms(8),
    paddingHorizontal: ms(30),
    textAlign: 'center',
  },
  inputContainer: {
    flex: 1,
    backgroundColor: '#F3F6F8',
    borderTopLeftRadius: ms(20),
    borderTopRightRadius: ms(20),
    paddingTop: ms(20),
  },
  scrollView: {
    padding: ms(20),
    paddingBottom: ms(40),
  },
  scrollViewContent: {
    paddingBottom: ms(350),
  },
  inputGroup: {
    marginBottom: ms(15),
  },
  inputWrapper: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#FFFFFF',
    borderRadius: ms(30),
    borderWidth: ms(1),
    borderColor: '#d8dbdd',
    paddingHorizontal: ms(15),
  },
  input: {
    flex: 1,
    height: s(50),
    fontSize: ms(16, 0.3),
    color: '#1C2C4F',
  },
  dropdownInput: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    backgroundColor: '#FFFFFF',
    borderRadius: ms(30),
    paddingHorizontal: ms(15),
    height: s(50),
    borderWidth: ms(1),
    borderColor: '#d8dbdd',
  },
  disabledDropdown: {
    backgroundColor: '#F5F5F5',
    borderColor: '#e8e8e8',
  },
  disabledIcon: {
    opacity: 0.5,
  },
  inputText: {
    fontSize: ms(16, 0.3),
    color: '#1C2C4F',
  },
  placeholderText: {
    color: '#A9A9A9',
  },
  dropdownIcon: {
    width: s(20),
    height: s(20),
    tintColor: '#1C2C4F',
  },
  lockIcon: {
    width: s(20),
    height: s(20),
    tintColor: '#1C2C4F',
  },
  submitButton: {
    backgroundColor: '#29BFC0',
    borderRadius: ms(25),
    padding: ms(15),
    alignItems: 'center',
    marginTop: ms(20),
  },
  disabledButton: {
    backgroundColor: '#CCCCCC',
  },
  submitButtonText: {
    color: '#FFFFFF',
    fontSize: ms(18, 0.3),
    fontWeight: '500',
  },
  notesInput: {
    height: s(80),
    textAlignVertical: 'top',
    paddingTop: ms(15),
  },
  bottomPadding: {
    height: s(150),
  },
  modalContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
  },
  modalContent: {
    backgroundColor: '#FFFFFF',
    borderRadius: ms(20),
    padding: ms(20),
    width: '80%',
    maxHeight: '80%',
  },
  modalTitle: {
    fontSize: ms(20, 0.3),
    fontWeight: 'bold',
    color: '#333333',
    marginBottom: ms(15),
    textAlign: 'center',
  },
  categoryItem: {
    padding: ms(15),
    borderBottomWidth: ms(1),
    borderBottomColor: '#EEEEEE',
  },
  categoryText: {
    fontSize: ms(16, 0.3),
    color: '#333333',
  },
  closeButton: {
    marginTop: ms(15),
    padding: ms(15),
    alignItems: 'center',
    backgroundColor: '#F3F6F8',
    borderRadius: ms(25),
  },
  closeButtonText: {
    color: '#333333',
    fontSize: ms(16, 0.3),
    fontWeight: '500',
  },
});
